import { useEffect,useMemo,useState } from "react";
import {
  Box,Typography,CircularProgress,FormControl,Select,MenuItem,
  Tabs,Tab,Divider
} from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const getTeamName=(team)=>{
  return team?.name||"Por definir";
};

const getTeamLogo=(team)=>{
  return team?.logo||null;
};

const getVenueName=(match)=>{
  return match?.venue?.name||"Cancha por definir";
};

const formatTime=(value)=>{
  if(!value) return "Hora por definir";
  return String(value).slice(0,5);
};

const formatDate=(value)=>{
  if(!value) return "Fecha por definir";

  const parts=String(value).split("-");

  if(parts.length!==3) return value;

  const date=new Date(
    Number(parts[0]),
    Number(parts[1])-1,
    Number(parts[2])
  );

  return new Intl.DateTimeFormat("es-EC",{
    weekday:"short",
    day:"2-digit",
    month:"short"
  }).format(date).replace(/\./g,"");
};

const formatPhase=(value)=>{
  if(value==="LEAGUE") return "Todos contra todos";
  if(value==="GROUPS") return "Fase de grupos";
  if(value==="KNOCKOUT") return "Eliminación directa";
  return value||"";
};

const getStatus=(match)=>{
  if(match?.status_display) return match.status_display;
  if(match?.status==="SCHEDULED") return "Programado";
  if(match?.status==="LIVE") return "En vivo";
  if(match?.status==="FINISHED") return "Finalizado";
  if(match?.status==="CANCELLED") return "Cancelado";
  if(match?.status==="SUSPENDED") return "Suspendido";
  return match?.status||"";
};

const getMatchResult=(match)=>{
  if(match?.status==="FINISHED"){
    return{
      finished:true,
      local:match.local_score??0,
      visitor:match.visitor_score??0
    };
  }

  return{
    finished:false,
    local:null,
    visitor:null
  };
};

export default function DelegateMatches(){
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [loading,setLoading]=useState(true);
  const [team,setTeam]=useState(null);
  const [tournaments,setTournaments]=useState([]);
  const [selectedTournament,setSelectedTournament]=useState("");
  const [selectedCategory,setSelectedCategory]=useState("");

  useEffect(()=>{
    const loadMatches=async()=>{
      try{
        setLoading(true);

        const response=await fetch("/matches/delegate/",{
          credentials:"include"
        });

        const data=await readResponse(response);

        if(!response.ok){
          throw new Error(
            data.detail||
            data.error||
            "No se pudo cargar el calendario de partidos."
          );
        }

        const tournamentRows=Array.isArray(data.tournaments)
          ?data.tournaments
          :[];

        setTeam(data.team||null);
        setTournaments(tournamentRows);

        if(tournamentRows.length>0){
          const firstTournament=tournamentRows[0];

          setSelectedTournament(firstTournament.slug);

          const firstCategory=Array.isArray(firstTournament.categories)
            ?firstTournament.categories[0]
            :null;

          setSelectedCategory(firstCategory?.slug||"");
        }else{
          setSelectedTournament("");
          setSelectedCategory("");
        }
      }catch(error){
        setTeam(null);
        setTournaments([]);
        setSelectedTournament("");
        setSelectedCategory("");

        showError(
          error.message||
          "No se pudo cargar el calendario de partidos."
        );
      }finally{
        setLoading(false);
      }
    };

    loadMatches();
  },[showError]);

  const currentTournament=useMemo(()=>{
    return tournaments.find(
      tournament=>tournament.slug===selectedTournament
    )||null;
  },[tournaments,selectedTournament]);

  const categories=useMemo(()=>{
    if(!currentTournament) return [];

    return Array.isArray(currentTournament.categories)
      ?currentTournament.categories
      :[];
  },[currentTournament]);

  const currentCategory=useMemo(()=>{
    return categories.find(
      category=>category.slug===selectedCategory
    )||null;
  },[categories,selectedCategory]);

  const matches=useMemo(()=>{
    if(!currentCategory) return [];

    return Array.isArray(currentCategory.matches)
      ?currentCategory.matches
      :[];
  },[currentCategory]);

  const phaseSections=useMemo(()=>{
    const result=[];

    matches.forEach(match=>{
      const phase=match.phase||{};
      const phaseSlug=phase.slug||"fase";

      let section=result.find(
        item=>item.slug===phaseSlug
      );

      if(!section){
        section={
          slug:phaseSlug,
          name:phase.name||"Fase",
          order:phase.order??0,
          format:phase.format||"",
          roundTrip:phase.round_trip===true,
          matches:[]
        };

        result.push(section);
      }

      section.matches.push(match);
    });

    return result.sort(
      (a,b)=>a.order-b.order
    );
  },[matches]);

  const handleTournamentChange=(event)=>{
    const slug=event.target.value;

    setSelectedTournament(slug);

    const tournament=tournaments.find(
      item=>item.slug===slug
    );

    const firstCategory=Array.isArray(tournament?.categories)
      ?tournament.categories[0]
      :null;

    setSelectedCategory(firstCategory?.slug||"");
  };

  const handleCategoryChange=(_,slug)=>{
    setSelectedCategory(slug);
  };

  if(loading){
    return(
      <Box
        sx={{
          minHeight:300,
          display:"flex",
          alignItems:"center",
          justifyContent:"center"
        }}
      >
        <CircularProgress
          size={26}
          sx={{
            color:colors.secondary
          }}
        />
      </Box>
    );
  }

  return(
    <Box
      sx={{
        width:"100%",
        maxWidth:1300,
        mx:"auto"
      }}
    >
      <Box
        sx={{
          mb:2,
          display:"flex",
          flexDirection:{
            xs:"column",
            md:"row"
          },
          alignItems:{
            xs:"stretch",
            md:"flex-end"
          },
          justifyContent:"space-between",
          gap:1.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{
                xs:typography.sizes.xl,
                md:typography.sizes.pageTitle||typography.sizes.title
              },
              fontWeight:700,
              color:colors.text
            }}
          >
            Mis partidos
          </Typography>

          <Typography
            sx={{
              mt:0.35,
              fontSize:typography.sizes.md,
              color:colors.textSecondary
            }}
          >
            {team
              ?`Calendario de ${team.name}`
              :"Consulta el calendario de tu equipo."
            }
          </Typography>
        </Box>

        {tournaments.length>0&&(
          <Box
            sx={{
              width:{
                xs:"100%",
                sm:330
              }
            }}
          >
            <Typography
              sx={{
                mb:0.5,
                fontSize:typography.sizes.sm,
                fontWeight:600,
                color:colors.textSecondary
              }}
            >
              Torneo
            </Typography>

            <FormControl
              fullWidth
              size="small"
            >
              <Select
                value={selectedTournament}
                onChange={handleTournamentChange}
                sx={{
                  backgroundColor:colors.surface,
                  borderRadius:shape?.radiusSmall||"8px",
                  color:colors.text,
                  "& .MuiOutlinedInput-notchedOutline":{
                    borderColor:colors.border
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline":{
                    borderColor:colors.secondary
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                    borderColor:colors.secondary
                  }
                }}
              >
                {tournaments.map(tournament=>(
                  <MenuItem
                    key={tournament.slug}
                    value={tournament.slug}
                  >
                    {tournament.name}
                    {tournament.season
                      ?` · ${tournament.season}`
                      :""
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {!team?(
        <EmptyState
          text="No tienes un equipo activo asignado."
          colors={colors}
          typography={typography}
        />
      ):tournaments.length===0?(
        <EmptyState
          text="Tu equipo todavía no participa en ningún torneo activo."
          colors={colors}
          typography={typography}
        />
      ):categories.length===0?(
        <EmptyState
          text="Tu equipo no tiene categorías activas en este torneo."
          colors={colors}
          typography={typography}
        />
      ):(
        <>
          {categories.length>1&&(
            <Box
              sx={{
                mb:2.5,
                borderBottom:`1px solid ${colors.border}`
              }}
            >
              <Tabs
                value={selectedCategory||false}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight:42,
                  "& .MuiTabs-indicator":{
                    height:3,
                    backgroundColor:colors.secondary
                  },
                  "& .MuiTab-root":{
                    minHeight:42,
                    px:2,
                    py:0.8,
                    textTransform:"none",
                    fontSize:typography.sizes.md,
                    fontWeight:600,
                    color:colors.textSecondary
                  },
                  "& .Mui-selected":{
                    color:`${colors.text} !important`
                  }
                }}
              >
                {categories.map(category=>(
                  <Tab
                    key={category.slug}
                    value={category.slug}
                    label={category.name}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {categories.length===1&&currentCategory&&(
            <Box
              sx={{
                mb:2.5,
                pb:1,
                borderBottom:`1px solid ${colors.border}`
              }}
            >
              <Typography
                sx={{
                  fontSize:typography.sizes.lg,
                  fontWeight:700,
                  color:colors.text
                }}
              >
                {currentCategory.name}
              </Typography>
            </Box>
          )}

          {matches.length===0?(
            <EmptyState
              text="Todavía no existen partidos para tu equipo en esta categoría."
              colors={colors}
              typography={typography}
            />
          ):(
            <Box
              sx={{
                display:"flex",
                flexDirection:"column",
                gap:3
              }}
            >
              {phaseSections.map(phase=>(
                <PhaseCalendar
                  key={phase.slug}
                  phase={phase}
                  colors={colors}
                  typography={typography}
                  shape={shape}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function PhaseCalendar({
  phase,
  colors,
  typography,
  shape
}){
  const groupedMatches=useMemo(()=>{
    const result=[];

    phase.matches.forEach(match=>{
      const groupSlug=
        match.group?.slug||
        "";

      const groupName=
        match.group?.name||
        "";

      const roundOrder=
        match.round?.order??
        0;

      const roundName=
        match.round?.name||
        "Jornada";

      const leg=
        match.round?.leg??
        1;

      let section=result.find(item=>
        item.groupSlug===groupSlug&&
        item.roundOrder===roundOrder&&
        item.leg===leg
      );

      if(!section){
        section={
          key:`${groupSlug||"phase"}-${roundOrder}-${leg}`,
          groupSlug,
          groupName,
          roundOrder,
          roundName,
          leg,
          matches:[]
        };

        result.push(section);
      }

      section.matches.push(match);
    });

    return result.sort((a,b)=>{
      if(a.groupName!==b.groupName){
        return a.groupName.localeCompare(
          b.groupName,
          "es",
          {
            sensitivity:"base",
            numeric:true
          }
        );
      }

      if(a.roundOrder!==b.roundOrder){
        return a.roundOrder-b.roundOrder;
      }

      return a.leg-b.leg;
    });
  },[phase.matches]);

  return(
    <Box>
      <Box
        sx={{
          mb:1.5,
          display:"flex",
          alignItems:{
            xs:"flex-start",
            sm:"flex-end"
          },
          justifyContent:"space-between",
          flexDirection:{
            xs:"column",
            sm:"row"
          },
          gap:0.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {phase.name}
          </Typography>

          <Typography
            sx={{
              mt:0.15,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            {formatPhase(phase.format)}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          {phase.matches.length} partido
          {phase.matches.length===1?"":"s"}
        </Typography>
      </Box>

      <Box
        sx={{
          display:"flex",
          flexDirection:"column",
          gap:2
        }}
      >
        {groupedMatches.map(section=>(
          <Box key={section.key}>
            <Box
              sx={{
                mb:0.7,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                gap:1
              }}
            >
              <Typography
                sx={{
                  fontSize:typography.sizes.md,
                  fontWeight:700,
                  color:colors.text
                }}
              >
                {section.groupName
                  ?`${section.groupName} · ${section.roundName}`
                  :section.roundName
                }
              </Typography>

              {phase.roundTrip&&(
                <Typography
                  sx={{
                    fontSize:typography.sizes.sm,
                    color:colors.textSecondary
                  }}
                >
                  {section.leg===2?"Vuelta":"Ida"}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                border:`1px solid ${colors.border}`,
                borderRadius:shape?.radius||"10px",
                backgroundColor:colors.surface,
                overflow:"hidden"
              }}
            >
              {section.matches.map((match,index)=>(
                <Box
                  key={match.code||`${section.key}-${index}`}
                >
                  {index>0&&(
                    <Divider
                      sx={{
                        borderColor:colors.border
                      }}
                    />
                  )}

                  <MatchRow
                    match={match}
                    colors={colors}
                    typography={typography}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MatchRow({
  match,
  colors,
  typography
}){
  const result=getMatchResult(match);

  return(
    <Box
      sx={{
        px:{
          xs:1.2,
          sm:2
        },
        py:1.5,
        display:"grid",
        gridTemplateColumns:{
          xs:"1fr",
          md:"155px minmax(180px,1fr) 80px minmax(180px,1fr) 170px"
        },
        alignItems:"center",
        gap:{
          xs:1.2,
          md:1.5
        },
        transition:"background-color .15s ease",
        "&:hover":{
          backgroundColor:colors.backgroundSoft
        }
      }}
    >
      <Box
        sx={{
          display:"flex",
          flexDirection:{
            xs:"row",
            md:"column"
          },
          justifyContent:{
            xs:"center",
            md:"flex-start"
          },
          alignItems:{
            xs:"center",
            md:"flex-start"
          },
          gap:{
            xs:1,
            md:0.15
          }
        }}
      >
        <Typography
          sx={{
            fontSize:typography.sizes.sm,
            fontWeight:600,
            color:colors.text
          }}
        >
          {formatDate(match.date)}
        </Typography>

        <Typography
          sx={{
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          {formatTime(match.start_time)}
        </Typography>
      </Box>

      <Team
        team={match.local}
        align="right"
        colors={colors}
        typography={typography}
      />

      <Box
        sx={{
          display:"flex",
          justifyContent:"center",
          alignItems:"center"
        }}
      >
        {result.finished?(
          <Box
            sx={{
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              gap:0.8,
              minWidth:70
            }}
          >
            <Typography
              sx={{
                fontSize:typography.sizes.xl,
                fontWeight:700,
                color:colors.text
              }}
            >
              {result.local}
            </Typography>

            <Typography
              sx={{
                fontSize:typography.sizes.sm,
                color:colors.textSecondary
              }}
            >
              -
            </Typography>

            <Typography
              sx={{
                fontSize:typography.sizes.xl,
                fontWeight:700,
                color:colors.text
              }}
            >
              {result.visitor}
            </Typography>
          </Box>
        ):(
          <Typography
            sx={{
              fontSize:typography.sizes.sm,
              fontWeight:700,
              color:colors.textSecondary
            }}
          >
            VS
          </Typography>
        )}
      </Box>

      <Team
        team={match.visitor}
        align="left"
        colors={colors}
        typography={typography}
      />

      <Box
        sx={{
          display:"flex",
          flexDirection:"column",
          alignItems:{
            xs:"center",
            md:"flex-end"
          },
          gap:0.15
        }}
      >
        <Typography
          sx={{
            maxWidth:170,
            textAlign:{
              xs:"center",
              md:"right"
            },
            fontSize:typography.sizes.sm,
            fontWeight:600,
            color:colors.text
          }}
        >
          {getVenueName(match)}
        </Typography>

        <Typography
          sx={{
            fontSize:typography.sizes.xs||typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          {getStatus(match)}
        </Typography>
      </Box>
    </Box>
  );
}

function Team({
  team,
  align,
  colors,
  typography
}){
  const logo=getTeamLogo(team);
  const right=align==="right";

  return(
    <Box
      sx={{
        display:"flex",
        alignItems:"center",
        justifyContent:{
          xs:"center",
          md:right?"flex-end":"flex-start"
        },
        gap:1
      }}
    >
      {right?(
        <>
          <Typography
            sx={{
              textAlign:{
                xs:"center",
                md:"right"
              },
              fontSize:typography.sizes.md,
              fontWeight:700,
              color:colors.text,
              lineHeight:1.3
            }}
          >
            {getTeamName(team)}
          </Typography>

          {logo&&(
            <Box
              component="img"
              src={logo}
              alt={getTeamName(team)}
              sx={{
                width:34,
                height:34,
                objectFit:"contain",
                flexShrink:0
              }}
            />
          )}
        </>
      ):(
        <>
          {logo&&(
            <Box
              component="img"
              src={logo}
              alt={getTeamName(team)}
              sx={{
                width:34,
                height:34,
                objectFit:"contain",
                flexShrink:0
              }}
            />
          )}

          <Typography
            sx={{
              textAlign:{
                xs:"center",
                md:"left"
              },
              fontSize:typography.sizes.md,
              fontWeight:700,
              color:colors.text,
              lineHeight:1.3
            }}
          >
            {getTeamName(team)}
          </Typography>
        </>
      )}
    </Box>
  );
}

function EmptyState({
  text,
  colors,
  typography
}){
  return(
    <Box
      sx={{
        py:5,
        px:2,
        borderTop:`1px solid ${colors.border}`,
        borderBottom:`1px solid ${colors.border}`,
        textAlign:"center"
      }}
    >
      <Typography
        sx={{
          fontSize:typography.sizes.md,
          color:colors.textSecondary
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}